import React from "react";

import { useTranslation } from "utils";

import { GoalScore } from "models";

import { Divider, Flex, IconLinkButton, OverflowEllipsisWithTooltip, Tag } from "components";

import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart.tsx";

export type MixItem = {
  name: string;
  weightShare: number;
};

export type TopMatchCardCommercialMix = {
  id: string;
  provider: string;
  name: string;
  matchScore: number;
  mixItem: MixItem[];
  goalScores: GoalScore[];
  link: string | null;
};

type TopMatchCardProps = {
  commercialMix: TopMatchCardCommercialMix;
  isPopover?: boolean;
};

export const TopMatchCard: React.FC<TopMatchCardProps> = ({ commercialMix, isPopover }) => {
  const { t } = useTranslation("cropMixComposer");

  const handleContactProvider = () => {
    if (commercialMix.link) {
      window.open(commercialMix.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Flex className={`top-match-card ${isPopover ? "popover" : ""}`} data-testid="top-match-card" vertical justify="space-evenly" gap={"2rem"}>
      {/* Header */}
      <Flex className="header" justify="space-between">
        <Flex vertical align="flex-start" flex={1} style={{ maxWidth: "14rem" }}>
          <h6 className="mix-name">{commercialMix.name}</h6>
          <p className="provider">{commercialMix.provider}</p>
        </Flex>
        <Flex vertical align="flex-end" gap={"0.25rem"}>
          <Tag className="tag" colour="yellow" text={t("topMatchCard.matchScore", { matchScore: commercialMix.matchScore })} />
          <p className="paragraph-xs">{t("topMatchCard.withYourMix")}</p>
        </Flex>
      </Flex>
      {/* Crop Mix */}
      <Flex className="crops-table" vertical>
        <Flex className="header margin-bottom-50" justify="space-between">
          <p className="paragraph-xs">{t("topMatchCard.cropsInMix")}</p>
          <p className="paragraph-xs">{t("topMatchCard.weightShare")}</p>
        </Flex>
        <Divider />
        <div className="content">
          {commercialMix.mixItem.map((mixItem) => (
            <React.Fragment key={mixItem.name}>
              <Flex justify={"space-between"}>
                <p className="paragraph-m">{mixItem.name}</p>
                <p className="paragraph-m-bold">{`${mixItem.weightShare}%`}</p>
              </Flex>
              <Divider />
            </React.Fragment>
          ))}
        </div>
      </Flex>
      {/* Match Scores */}
      <Flex className="match-scores" justify="space-evenly" gap="0.5rem">
        {commercialMix.goalScores.map((goalScore, index) => (
          <Flex key={goalScore.name} align="center" vertical flex="1">
            <ScoreCircleChart
              dataTestId={`score-circle-chart-goalScore${index + 1}`}
              score={goalScore.score}
              color={Object.values(ScoreColor)[index]}
              size={50}
            />
            <Flex align="center">
              <OverflowEllipsisWithTooltip className="goal-label" title={t(`goals.${goalScore.name}`)} verticalOverflow>
                {t(`goals.${goalScore.name}`)}
              </OverflowEllipsisWithTooltip>
            </Flex>
          </Flex>
        ))}
        {commercialMix.goalScores.length === 0 && <div></div>}
      </Flex>
      {/* Footer */}
      {!isPopover && (
        <Flex className="footer" justify="flex-end">
          <IconLinkButton disabled={commercialMix.link === null} iconType="logout" text={t("topMatchCard.contactProvider")} onClick={handleContactProvider} />
        </Flex>
      )}
    </Flex>
  );
};
